import 'twin.macro';

import { Cross } from '@cheqroom/icons';
import {
	ComponentPropsWithoutRef,
	DetailedHTMLProps,
	FC,
	forwardRef,
	ForwardRefExoticComponent,
	InputHTMLAttributes,
	ReactNode,
	RefAttributes,
	useId,
} from 'react';

import { Icon, IconSource } from '../Icon/Icon';
import { Labelled, Props as LabelledProps } from '../Labelled/Labelled';
import { Stack } from '../Stack/Stack';
import { withTooltip } from '../Tooltip/Tooltip';
import { InputSkeleton } from './Input.skeleton';
import { Prefix, StyledInput, StyledInputWrapper, Suffix } from './Input.styles';

export interface Props extends Omit<ComponentPropsWithoutRef<'input'>, 'prefix' | 'suffix'>, LabelledProps {
	/**
	 * Determine type of input
	 * @default 'text'
	 */
	type?: 'text' | 'number' | 'email' | 'password' | 'date' | 'search';
	/** Show clear button */
	clearButton?: boolean;
	/** Inner icon button component to show. e.g Clear button, Copy button */
	innerIconButton?: ReactNode;
	/** Outer icon button component to show */
	outerIconButton?: ReactNode;
	/** Callback when value is cleared */
	onClear?: () => void;
	/**
	 * Placeholder text
	 */
	placeholder?: string;
	/** Text to display before value */
	prefix?: ReactNode;
	/** Text to display after value */
	suffix?: ReactNode;
	/** Show ellipsis when the text overflows the input */
	ellipsis?: boolean;
	/** Props to pass to the container */
	containerProps?: Record<string, unknown>;
	inputProps?: DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
	propagate?: boolean;
	/** Prevents the input from changing when scrolling */
	preventChangeOnScroll?: boolean;
	/** Disable autocomplete */
	disableAutocomplete?: boolean;
	/** Tooltip content */
	tooltipContent?: string;
}

interface Composition {
	Skeleton: typeof InputSkeleton;
}

const Input = forwardRef<HTMLInputElement, Props>(
	(
		{
			clearButton = false,
			innerIconButton,
			outerIconButton,
			containerProps,
			inputProps,
			error,
			warning,
			helpText,
			id,
			label,
			labelHidden,
			onChange,
			onClear,
			placeholder,
			prefix,
			readOnly,
			required,
			suffix,
			ellipsis = true,
			tabIndex,
			type = 'text',
			value,
			propagate = false,
			preventChangeOnScroll = false,
			disableAutocomplete,
			tooltipContent,
			...rest
		},
		ref
	) => {
		const generatedId = useId();

		// Create clear button component if clearButton is enabled
		const ClearButtonComponent =
			clearButton && onClear && !!value ? (
				<button
					type="button"
					aria-label="Clear"
					onClick={(e) => {
						e.stopPropagation();
						onClear();
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.stopPropagation();
							onClear();
						}
					}}
				>
					<Icon
						source={Cross as IconSource}
						size="small"
						tw="text-gray-500 cursor-pointer focus:outline-none"
					/>
				</button>
			) : null;

		// Use clear button if enabled, otherwise use the provided innerIconButton component
		const InnerButton = ClearButtonComponent || innerIconButton;

		// Render outerIconButton component if provided
		const OuterButton = outerIconButton || innerIconButton;

		const inputElement = (
			<StyledInput
				{...rest}
				{...inputProps}
				onClick={(event) => {
					event.stopPropagation();
					rest.onClick?.(event);
				}}
				onKeyDown={(event) => {
					!propagate && event.stopPropagation();
					rest.onKeyDown?.(event);
				}}
				onKeyUp={(event) => {
					!propagate && event.stopPropagation();
					rest.onKeyUp?.(event);
				}}
				id={id ?? generatedId}
				onChange={onChange}
				placeholder={placeholder}
				readOnly={readOnly}
				ellipsis={ellipsis}
				ref={ref}
				required={required}
				tabIndex={tabIndex}
				type={type}
				value={value}
				onWheel={(event) => {
					preventChangeOnScroll && event.currentTarget.blur();
					rest.onWheel?.(event);
				}}
				autoComplete={disableAutocomplete ? 'off' : 'on'}
			/>
		);

		return (
			<Labelled
				error={error}
				warning={warning}
				helpText={helpText}
				htmlFor={id ?? generatedId}
				label={label}
				labelHidden={labelHidden}
				required={required}
				{...containerProps}
				{...rest}
			>
				<Stack tw="gap-2">
					<StyledInputWrapper error={error} warning={warning} readOnly={readOnly}>
						{prefix && <Prefix>{prefix}</Prefix>}

						{withTooltip(inputElement, tooltipContent ?? '', !!tooltipContent)}

						{suffix && <Suffix>{suffix}</Suffix>}

						{InnerButton}
					</StyledInputWrapper>

					{OuterButton}
				</Stack>
			</Labelled>
		);
	}
) as ForwardRefExoticComponent<Props & RefAttributes<HTMLInputElement>> & Composition;

Input.displayName = 'Input';

Input.Skeleton = InputSkeleton;

export { Input };
